/**
 * Copyright 2018 Samsung Electronics All Rights Reserved
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import frest from 'frest';
import { Omit } from 'utility-types';
import { AuthProviderProps } from '@swuif/auth';
import {
  SbaasPresetOptions,
  SbaasPreset,
  sbaasResponseTransformer,
} from '../common';
import { sbaasAuthStorage } from '../common/auth';
import {
  sbaasLegacyAuthErrorInterceptor,
  sbaasLegacyRequestInterceptor,
} from './http';
import { createSbaasLegacyClient } from './client';

export const sbaasLegacyPreset = ({
  sbaas,
  ...options
}: SbaasPresetOptions): SbaasPreset => {
  const authStorage =
    options.auth && options.auth.storage
      ? options.auth.storage
      : sbaasAuthStorage(options.config.app.appName);

  const http = frest.create({
    base: sbaas.base,
    credentials: 'same-origin',
    headers: sbaas.defaultHeaders,
  });
  if (!sbaas.noLowerCaseJSON) {
    http.mergeConfig({ transformResponse: [sbaasResponseTransformer] });
  }
  http.interceptors.request.use(sbaasLegacyRequestInterceptor(authStorage));
  http.interceptors.error.use(sbaasLegacyAuthErrorInterceptor(authStorage));

  const auth: Omit<Partial<AuthProviderProps>, 'children'> = {
    ...options.auth,
    storage: authStorage,
  };

  const client = createSbaasLegacyClient(http, sbaas.app);

  return {
    app: {
      auth,
      config: options.config,
    },
    http,
    sbaas: client,
  };
};
